<template>
  <div class="purchase-carton-container border-bottom">
    <!-- eslint-disable max-len -->
    <Notification
      v-if="
        shipment.shipmentType === 'S2AP' &&
        (shipment?.shipmentStatusCode === 604 || shipment?.shipmentStatusCode === 605) &&
        shipment.shipmentReRouted
      "
      severity="info"
      :closable="false"
    >
      {{ ORDER_HISTORY.shipmentReRoutedMessage }}
      <NuxtLink
        class="track-package-cta link-body"
        :to="{ name: 'Track Package', query: { o: shipment.orderToken, s: shipment.shipmentToken, trackingNos: shipment.trackingNos } }"
      >
        {{ ORDER_HISTORY.trackPackageLabel }}
      </NuxtLink>
    </Notification>
    <div class="purchase-carton padding-top-m">
      <div class="cartons grid-x align-justify">
        <div class="cell auto margin-right-xs" :class="{ 'small-12 medium-12 margin-bottom-s': groupItems }">
          <h2 class="h4 order-status margin-bottom-xxs" v-text="shipment.shipmentStatus"></h2>
          <template v-if="shipment.shipmentStatusCode === 401">
            <div v-if="shipment.shipmentTotal" class="return-date margin-bottom-xxs">
              <span class="price-reg"> {{ shipment.shipmentTotal }} On {{ shipment.shipmentConfirmationDate }}</span>
            </div>

            <div v-if="shipment.storeName" class="store-name">
              {{ shipment.storeName }}
            </div>
          </template>
          <template v-else>
            <div
              v-if="shipment.shipmentSubStatus"
              class="arrive-date medium"
              :class="{ 'margin-bottom-xxs': !shipment.shipmentSubStatusDetails }"
              v-text="shipment.shipmentSubStatus"
            ></div>
            <div
              v-if="shipment.shipmentSubStatusDetails"
              class="margin-bottom-xxs small gray-1-color"
              v-text="shipment.shipmentSubStatusDetails"
            ></div>
          </template>
        </div>
        <div class="cell" :class="{ 'small-12 medium-9': disableitemDetailCTA, 'shrink grid-x align-right align-middle': !groupItems }">
          <div v-if="shipment?.items?.length" :class="!hideitemDetailsCTA && !groupItems ? 'medium-margin-right-xxl' : ''">
            <div class="cell grid-x" :class="{ 'align-right': !groupItems, 'image-wrapper': groupItems }">
              <div
                v-for="(item, index) in computeShipmentItems"
                :key="index"
                class="item-image-container"
                :class="{ 'small-margin-bottom-xs medium-margin-bottom-s small-margin-right-xs medium-margin-right-s': groupItems }"
              >
                <picture v-if="item?.urlTemplate && item?.primaryImageFilePath" :class="{ 'margin-right-xs': !groupItems }">
                  <source
                    media="(min-width: 320px)"
                    :srcset="item.urlTemplate + item.primaryImageFilePath"
                    :alt="item.productName || item.itemDescription"
                  />
                  <a v-if="item.productId" :href="`/shop/product/?ID=${item.productId}`" @click.stop>
                    <img :src="item.urlTemplate + item.primaryImageFilePath" :alt="item.productName || item.itemDescription" />
                  </a>
                  <img v-else :src="item.urlTemplate + item.primaryImageFilePath" :alt="item.productName || item.itemDescription" />
                </picture>
                <FallbackImage v-else class="fallback-image margin-right-xs" />
                <div v-show="shipment.items.length <= 3 && item.quantity > 1" class="qty-container">
                  <span class="number-badge qty-circle"></span>
                  <span class="qty small">{{ item.quantity }}</span>
                </div>
              </div>
            </div>
            <span v-if="shipment.protectionPlanLabel" class="medium-9 protectionPlan">
              {{ shipment.protectionPlanLabel }}
            </span>
            <div v-if="!groupItems" class="cell grid-x align-right">
              <div v-if="shipment.items.length > 2" class="margin-right-xs">
                +{{ shipment.items.length - 2 }} {{ shipment.items.length === 3 ? 'item' : 'items' }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="!hideitemDetailsCTA" class="cell small-12 medium-3 align-middle">
          <div>
            <div v-if="!readOnly">
              <NuxtLink
                v-if="
                  buttonConfig.label &&
                  [ORDER_HISTORY.returnDetailsLabel, ORDER_HISTORY.exchangeDetailsLabel, ORDER_HISTORY.replacementDetailsLabel].includes(
                    buttonConfig.label,
                  )
                "
                class="order-detail-action secondary button button-small expanded small-margin-top-s medium-margin-top-z"
                :to="buttonConfig.action"
                :aria-label="`View ${buttonConfig.label.toLowerCase()} for this order`"
              >
                {{ buttonConfig.label }}
              </NuxtLink>
              <NuxtLink
                v-else-if="buttonConfig.label"
                class="order-detail-action secondary button button-small expanded small-margin-top-s medium-margin-top-z"
                :to="buttonConfig.action"
                :aria-label="`View ${buttonConfig.label.toLowerCase()} for this order`"
                @click="!readOnly && shipment.shipmentStatusCode === 401 && setSelectedShipment(shipment)"
              >
                {{ buttonConfig.label }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useInstoreOrderHistoryStore } from '@/stores/ppe/useInstoreOrderHistoryStore';
import { useOrderHistoryStore } from '@/stores/ppe/useOrderHistoryStore';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import FallbackImage from '@/components/common/FallbackImage.vue';
import { ORDER_HISTORY } from 'config';

const props = defineProps({
  shipment: {
    type: Object,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  itemDetailsDisabled: {
    type: Boolean,
    default: false,
  },
  groupItems: {
    type: Boolean,
    default: false,
  },
});
const instoreOrderHistoryStore = useInstoreOrderHistoryStore();
const orderHistoryStore = useOrderHistoryStore();

const setSelectedShipment = (shipment) => {
  instoreOrderHistoryStore.SELECTED_SHIPMENT(shipment);
};

const computeShipmentItems = computed(() => (props.groupItems ? props.shipment.items : props.shipment.items.slice(0, 2)));
const hideitemDetailsCTA = computed(
  () => (props.shipment.shipmentStatusCode === 304 || props.shipment.shipmentState?.toLowerCase() === 'inactive') && props.itemDetailsDisabled,
);
const disableitemDetailCTA = computed(() => props.groupItems && !hideitemDetailsCTA.value);
const isSelfServiceReplacementExchangeEnabled = computed(() => orderHistoryStore.killSwitch?.selfServiceReplacementExchangeEnabled);
const receiptNumber = computed(() => {
  switch (props.shipment.shipmentStatus) {
    case 'Returned':
    case 'Purchased':
    case 'Exchange':
      return props.shipment.receiptIdentifier || props.shipment.receiptTimestamp;
    default:
      return '';
  }
});
const buttonConfig = computed(() => {
  let routerObject = {};
  let buttonLabel = '';
  const exchangeOrReplacementEligible = isSelfServiceReplacementExchangeEnabled.value && props.shipment?.exchangeType?.toLowerCase() === 'ssre';
  if (exchangeOrReplacementEligible && !props.shipment?.pendingReturnId) {
    return { action: routerObject, label: buttonLabel };
  }
  if (props.shipment.shipmentStatusCode === 401) {
    buttonLabel = ORDER_HISTORY.purchaseDetailsLabel;
    routerObject = {
      name: ORDER_HISTORY.purchaseDetailsLabel,
      params: { receiptNumber: receiptNumber.value },
    };
  } else if ([300, 301, 302, 303, 305].includes(props.shipment.shipmentStatusCode)) {
    if (props.shipment.pendingReturnId && props.shipment.shipmentStatusCode !== 303) {
      buttonLabel = ORDER_HISTORY.returnDetailsLabel;
      routerObject = {
        name: ORDER_HISTORY.returnDetailsLabel,
        query: {
          pid: props.shipment.pendingReturnId,
          orderNumber: props.shipment.orderNumber?.[0],
        },
      };
    }
  } else if (
    isSelfServiceReplacementExchangeEnabled.value &&
    exchangeOrReplacementEligible &&
    props.shipment.items[0].exchangeSubType &&
    props.shipment?.pendingReturnId
  ) {
    buttonLabel =
      props.shipment.items[0].exchangeSubType.toLowerCase() === 'exchange'
        ? ORDER_HISTORY.exchangeDetailsLabel
        : ORDER_HISTORY.replacementDetailsLabel;
    routerObject = {
      name: ORDER_HISTORY.returnDetailsLabel,
      query: {
        pid: props.shipment.pendingReturnId,
        orderNumber: props.shipment.orderNumber?.[0],
      },
    };
  } else {
    buttonLabel = ['BOPS', 'BOSS'].includes(props.shipment?.shipmentType) ? ORDER_HISTORY.trackPickUpLabel : ORDER_HISTORY.trackPackageLabel;
    routerObject = {
      name: ORDER_HISTORY.trackPackageLabel,
      query: {
        o: props.shipment.orderToken,
        s: props.shipment.shipmentToken,
        trackingNos: props.shipment.trackingNos,
      },
    };
  }
  return { action: routerObject, label: buttonLabel };
});
</script>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_delivery-truck.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_warning-filled-red.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_same-day-delivery-truck.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_order-history.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_bops.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_store-mode.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_confirmation-black-filled.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_bag-large-star.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_number-badge.scss';

.purchase-carton {
  .item-image-container {
    position: relative;
    picture img,
    .fallback-image {
      width: rem-calc(88);
    }
    .fallback-image {
      height: rem-calc(100);
    }
    @media screen and (max-width: 1024px) {
      picture img,
      .fallback-image {
        width: rem-calc(58);
      }
      .fallback-image {
        height: rem-calc(72);
      }
    }
    .qty-container {
      position: absolute;
      top: rem-calc(-20);
      right: rem-calc(-4);

      .qty {
        position: absolute;
        right: rem-calc(8);
      }
    }
  }
  @include breakpoint(small only) {
    .image-wrapper {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
    }
  }
  .image-wrapper {
    .item-image-container {
      @include breakpoint(medium down) {
        picture img,
        .fallback-image {
          width: rem-calc(68);
        }
      }
      .fallback-image {
        height: rem-calc(82);
      }
    }
  }
}

.purchase-carton-container {
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}
</style>
<style lang="scss" brand="mcom" scoped>
.purchase-carton {
  .item-image-container {
    .qty-container {
      .qty {
        top: rem-calc(2);
      }
    }
  }
}
</style>
<style lang="scss" brand="bcom" scoped>
.purchase-carton {
  .item-image-container {
    .qty-container {
      .qty {
        top: rem-calc(5);
      }
    }
  }
}
</style>
